import { useState, useEffect } from 'react'
import { useUser } from '../contexts/UserContext'
import { getPendingStories, approveStory, PendingStory } from '../api/admin'
import { Check, Calendar, User, Mail } from 'lucide-react'

const AdminStoriesPage = () => {
  const { user } = useUser()
  const [pendingStories, setPendingStories] = useState<PendingStory[]>([])
  const [loading, setLoading] = useState(true)
  const [approving, setApproving] = useState<string | null>(null)

  useEffect(() => {
    const fetchPendingStories = async () => {
      try {
        const stories = await getPendingStories()
        setPendingStories(stories)
      } catch (error) {
        console.error('Error fetching pending stories:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchPendingStories()
  }, [])

  const handleApprove = async (storyId: string, isPublished: boolean = true) => {
    setApproving(storyId)
    try {
      const success = await approveStory(storyId, isPublished)
      if (success) {
        // Remove the approved story from the list
        setPendingStories(prev => prev.filter(story => story.id !== storyId))
      }
    } catch (error) {
      console.error('Error approving story:', error)
    } finally {
      setApproving(null)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Check if user is admin
  if (user?.userType !== 'ADMIN') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You need admin privileges to access this page.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading pending stories...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Story Approval</h1>
          <p className="text-gray-600">
            Review and approve stories submitted by users before they appear on the blog.
          </p>
        </div>

        {pendingStories.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">✅</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Pending Stories</h3>
            <p className="text-gray-600">
              All stories have been reviewed. Check back later for new submissions.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {pendingStories.map((story) => (
              <div key={story.id} className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{story.title}</h2>
                    <div className="flex items-center text-sm text-gray-500 space-x-4 mb-3">
                      <div className="flex items-center">
                        <User className="h-4 w-4 mr-1" />
                        {story.authorName}
                      </div>
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 mr-1" />
                        {story.author.email}
                      </div>
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formatDate(story.createdAt)}
                      </div>
                    </div>
                    {story.excerpt && (
                      <p className="text-gray-700 mb-4">{story.excerpt}</p>
                    )}
                  </div>
                </div>

                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Content Preview:</h3>
                  <div className="bg-gray-50 rounded-lg p-4 max-h-40 overflow-y-auto">
                    <p className="text-gray-800 whitespace-pre-wrap">
                      {story.content.substring(0, 500)}
                      {story.content.length > 500 && '...'}
                    </p>
                  </div>
                </div>

                {story.tags.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Tags:</h3>
                    <div className="flex flex-wrap gap-2">
                      {story.tags.map((tag) => (
                        <span key={tag} className="px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => handleApprove(story.id, false)}
                    disabled={approving === story.id}
                    className="flex items-center px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    {approving === story.id ? 'Approving...' : 'Approve (Draft)'}
                  </button>
                  <button
                    onClick={() => handleApprove(story.id, true)}
                    disabled={approving === story.id}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    {approving === story.id ? 'Approving...' : 'Approve & Publish'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminStoriesPage
